import './Main.css';
/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
/* Theme variables */
import './theme/variables.css';

import {
  IonApp,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonMenuButton,
  IonModal,
  IonRoute,
  IonRouterOutlet,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToggle,
  IonToolbar,
  setupIonicReact,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import {
  closeOutline,
  contrastOutline,
  languageOutline,
  settingsOutline,
} from 'ionicons/icons';
import React, { lazy, Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import pr from './assets/images/pr-icon-min.png';
import usa from './assets/images/usa-icon-min.png';
import RouteFallback from './components/RouteFallback';
import { useAppStore } from './store/appStore';
import { LANGUAGE_PR, LANGUAGE_USA } from './util/constants';

const Menu = lazy(() => import('./components/Menu'));
const About = lazy(() => import('./pages/about'));
const Home = lazy(() => import('./pages/home'));
const Instruments = lazy(() => import('./pages/instruments'));
const Cuatro = lazy(() => import('./pages/instruments/cuatro'));
const Guitar = lazy(() => import('./pages/instruments/guitar'));
const Cagueno = lazy(() => import('./pages/instruments/guitar/cagueno'));
const Chorreado = lazy(() => import('./pages/instruments/guitar/chorreado'));
const Fajardeno = lazy(() => import('./pages/instruments/guitar/fajardeno'));
const Jibaro = lazy(() => import('./pages/instruments/guitar/jibaro'));
const Parranda = lazy(() => import('./pages/parranda'));
const Song = lazy(() => import('./pages/songbook/Song'));
const SongList = lazy(() => import('./pages/songbook/SongList'));

setupIonicReact({
  swipeBackEnabled: false,
});

const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
const MODAL_TRIGGER_ID = 'open-options';
const DARK_THEME_CLASS = 'dark';

const changeLanguage = (i18n: any, newLang: string): void => {
  i18n.changeLanguage(newLang);
};

const toggleTheme = (shouldAdd: boolean): void => {
  document.body.classList.toggle(DARK_THEME_CLASS, shouldAdd);
};

// Initial theme setup
toggleTheme(prefersDark.matches);

const SettingsModal: React.FC<{
  modalRef: React.RefObject<any>;
  t: any;
  i18n: any;
}> = ({ modalRef, t, i18n }) => {
  const [isDark, setIsDark] = useState(() =>
    document.body.classList.contains(DARK_THEME_CLASS),
  );

  const handleThemeChange = (checked: boolean) => {
    setIsDark(checked);
    toggleTheme(checked);
  };

  const handleLanguageChange = (value: unknown) => {
    if (value === LANGUAGE_USA || value === LANGUAGE_PR) {
      changeLanguage(i18n, value);
    }
  };

  return (
    <IonModal ref={modalRef} trigger={MODAL_TRIGGER_ID} className="settings-modal">
      <IonHeader>
        <IonToolbar color="primary">
          <IonTitle className="toolbar-title">{t('settings')}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={() => modalRef.current?.dismiss()}
              color="light"
              fill="clear">
              <IonIcon slot="icon-only" icon={closeOutline} className="color-white" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="settings-modal__content">
        <IonList inset className="settings-list">
          <IonItem lines="full">
            <IonIcon slot="start" icon={contrastOutline} />
            <IonLabel>
              <h2>{t('theme')}</h2>
            </IonLabel>
            <IonToggle
              slot="end"
              checked={isDark}
              enableOnOffLabels
              onIonChange={(e) => handleThemeChange(e.detail.checked)}
            />
          </IonItem>

          <IonItem lines="none">
            <IonIcon slot="start" icon={languageOutline} />
            <IonLabel>
              <h2>{t('language')}</h2>
              <IonSegment
                className="settings-language-segment"
                value={i18n.language}
                onIonChange={(e) => handleLanguageChange(e.detail.value)}>
                <IonSegmentButton value={LANGUAGE_USA} aria-label="English">
                  <IonImg src={usa} className="settings-flag" alt="English" />
                </IonSegmentButton>
                <IonSegmentButton value={LANGUAGE_PR} aria-label="Español">
                  <IonImg src={pr} className="settings-flag" alt="Español" />
                </IonSegmentButton>
              </IonSegment>
            </IonLabel>
          </IonItem>
        </IonList>
      </IonContent>
    </IonModal>
  );
};

const App: React.FC = () => {
  const header = useAppStore((state) => state.header);
  const { i18n, t } = useTranslation();
  const modal = useRef<any>(null);

  return (
    <IonApp>
      <IonHeader className="app-header">
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonButton id={MODAL_TRIGGER_ID} slot="end" style={{ marginRight: '16px' }}>
            <IonIcon slot="icon-only" icon={settingsOutline} />
          </IonButton>
          <SettingsModal modalRef={modal} t={t} i18n={i18n} />
          <IonTitle className="toolbar-title">{header}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonReactRouter>
        <Menu />
        <Suspense fallback={<RouteFallback />}>
          <IonRouterOutlet id="main" animated={false}>
            <IonRoute path="/" element={<Navigate to="/home" />} />
            <IonRoute path="/home" element={<Home />} />
            <IonRoute path="/songbook" element={<SongList />} />
            <IonRoute path="/song/:name" element={<Song />} />
            <IonRoute path="/parranda" element={<Parranda />} />
            <IonRoute path="/about" element={<About />} />
            <IonRoute path="/instruments" element={<Instruments />} />
            <IonRoute path="/instruments/guitar" element={<Guitar />} />
            <IonRoute
              path="/instruments/guitar/cagueno"
              element={<Cagueno />}
            />
            <IonRoute
              path="/instruments/guitar/fajardeno"
              element={<Fajardeno />}
            />
            <IonRoute path="/instruments/guitar/jibaro" element={<Jibaro />} />
            <IonRoute
              path="/instruments/guitar/chorreado"
              element={<Chorreado />}
            />
            <IonRoute path="/instruments/cuatro" element={<Cuatro />} />
          </IonRouterOutlet>
        </Suspense>
      </IonReactRouter>
    </IonApp>
  );
};

export default App;
